import apiClient from './apiClient';

export interface Admin {
  id: number;
  name: string;
  email: string;
  document?: string;
  company_name?: string;
  phone?: string;
  status: 'active' | 'blocked' | 'super';
  created_at: string;
  subscriptions?: AdminSubscription[];
  subscription?: AdminSubscription;
  plan?: SubscriptionPlan;
}

export interface AdminSubscription {
  id: number;
  admin_id: number;
  plan_id: number;
  expiration_date: string;
  status: 'active' | 'expired' | 'canceled';
  plan?: SubscriptionPlan;
}

export interface SubscriptionPlan {
  id: number;
  name: string;
  plan_type: 'basic' | 'professional' | 'enterprise';
  description?: string;
  price?: number;
}

export interface CreateAdminRequest {
  name: string;
  email: string;
  password: string;
  company_name?: string;
  phone?: string;
  plan_type?: string;
}

export interface UpdateAdminRequest {
  name?: string;
  email?: string;
  company_name?: string;
  phone?: string;
  status?: 'active' | 'blocked' | 'super';
}

export interface UpdateSubscriptionRequest {
  plan_id: number;
  expiration_date: string;
  plan_type?: string;
  status?: string;
}

export interface Plan {
  id: number;
  name: string;
  plan_type: 'basic' | 'professional' | 'enterprise';
  description?: string;
  price?: number;
  max_bots?: number;
  max_attendants?: number;
  features?: string[];
}

class AdminManagementService {
  private baseURL = '/admin';

  /**
   * Obter lista de administradores
   */
  async getAdmins(): Promise<Admin[]> {
    try {
      const response = await apiClient.get(`${this.baseURL}/`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao obter lista de administradores');
    }
  }

  /**
   * Obter lista de administradores com assinaturas
   */
  async getAdminsWithSubscriptions(): Promise<{ admins: Admin[]; pagination: any }> {
    try {
      const response = await apiClient.get(`${this.baseURL}/with-subscriptions`);
      const data = response.data?.data || {};

      // Garantir formato consistente para o frontend
      return {
        admins: Array.isArray(data.admins) ? data.admins : [],
        pagination: data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 1
        }
      };
    } catch (error: any) {
      // Preservar detalhes do backend quando existirem
      const backendMessage =
        error.response?.data?.message ||
        error.response?.data?.error;

      throw new Error(backendMessage || 'Erro ao obter administradores com assinaturas');
    }
  }

  /**
   * Criar novo administrador
   */
  async createAdmin(data: CreateAdminRequest): Promise<Admin> {
    try {
      const response = await apiClient.post(`${this.baseURL}/`, data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao criar administrador');
    }
  }

  /**
   * Atualizar administrador
   */
  async updateAdmin(id: number, data: UpdateAdminRequest): Promise<Admin> {
    try {
      const response = await apiClient.put(`${this.baseURL}/${id}`, data);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar administrador');
    }
  }

  /**
   * Deletar administrador
   */
  async deleteAdmin(id: number): Promise<void> {
    try {
      await apiClient.delete(`${this.baseURL}/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao deletar administrador');
    }
  }

  /**
   * Atualizar assinatura do administrador
   */
  async updateAdminSubscription(id: number, data: UpdateSubscriptionRequest): Promise<void> {
    try {
      await apiClient.put(`${this.baseURL}/${id}/subscription`, data);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao atualizar assinatura');
    }
  }

  /**
   * Obter dados de um admin específico para criação de fatura
   */
  async getAdminForInvoice(targetAdminId: number): Promise<Admin> {
    try {
      const response = await apiClient.get(`${this.baseURL}/for-invoice/${targetAdminId}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao obter dados do admin para fatura');
    }
  }
}

export const adminManagementService = new AdminManagementService();
